/**
 * Loads the strategic tuning (spec §4.9): parses every `src/data/strategic/*.json` once with the Zod schemas
 * in `schemas.ts`, cross-checks references between files (ware, module, template, hull and faction ids),
 * and resolves the index tables the hot loops use (recipes as per-ware arrays, base prices, hull costs).
 * Throws a ZodError naming the bad field, or an Error naming the file and the bad reference.
 */
import combatJson from '../data/strategic/combat.json'
import sinksJson from '../data/strategic/sinks.json'
import economySeedJson from '../data/strategic/economy-seed.json'
import factionsJson from '../data/strategic/factions.json'
import gateJson from '../data/strategic/gate.json'
import hullsJson from '../data/strategic/hulls.json'
import hullLinesJson from '../data/strategic/hull-lines.json'
import jobsJson from '../data/strategic/jobs.json'
import localJson from '../data/strategic/local.json'
import modulesJson from '../data/strategic/modules.json'
import rolesJson from '../data/strategic/roles.json'
import strategicJson from '../data/strategic/strategic.json'
import templatesJson from '../data/strategic/templates.json'
import travelJson from '../data/strategic/travel.json'
import waresJson from '../data/strategic/wares.json'
import wireJson from '../data/strategic/wire.json'
import aiJson from '../data/strategic/ai.json'
import claimsJson from '../data/strategic/claims.json'
import ladderJson from '../data/strategic/ladder.json'
import lawJson from '../data/strategic/law.json'
import outlawsJson from '../data/strategic/outlaws.json'
import statesJson from '../data/strategic/states.json'
import votesJson from '../data/strategic/votes.json'
import zonesJson from '../data/strategic/zones.json'
import fleetJson from '../data/strategic/fleet.json'
import ordersJson from '../data/strategic/orders.json'
import captainsJson from '../data/strategic/captains.json'
import corpJson from '../data/strategic/corp.json'
import commissionJson from '../data/strategic/commission.json'
import wireM6Json from '../data/strategic/wire-m6.json'
import {
  captainsConfigSchema,
  commissionConfigSchema,
  corpConfigSchema,
  fleetConfigSchema,
  ordersConfigSchema,
  wireM6ConfigSchema,
  type CaptainsConfig,
  type CommissionConfig,
  type CorpConfig,
  type FleetConfig,
  type OrdersConfig,
  type WireM6Config,
  aiConfigSchema,
  claimsConfigSchema,
  ladderConfigSchema,
  lawConfigSchema,
  outlawsConfigSchema,
  statesConfigSchema,
  votesConfigSchema,
  zonesConfigSchema,
  type AiConfig,
  type ClaimsConfig,
  type LadderConfig,
  type LawConfig,
  type OutlawsConfig,
  type StatesConfig,
  type VotesConfig,
  type ZonesConfig,
  combatConfigSchema,
  economySeedFileSchema,
  factionsFileSchema,
  gateConfigSchema,
  hullsFileSchema,
  jobsFileSchema,
  localConfigSchema,
  modulesFileSchema,
  rolesFileSchema,
  strategicConfigSchema,
  templatesFileSchema,
  travelConfigSchema,
  waresFileSchema,
  wireConfigSchema,
  type CombatConfig,
  type EconomySeedFile,
  type FactionsFile,
  type GateConfig,
  type HullsFile,
  type JobsFile,
  type LocalConfig,
  type ModulesFile,
  type RolesFile,
  type StrategicConfig,
  type TemplatesFile,
  type TravelConfig,
  type WaresFile,
  type WireConfig,
} from './schemas'
import {
  HULL_CLASS,
  PLAYER_SLOT,
  ROLE_IDS,
  SHIP_CLASS,
  SHIP_CLASS_NAMES,
  STATION_TYPE,
  STATION_TYPE_COUNT,
  STATION_TYPE_NAMES,
  TRAIT_COUNT,
  TRAIT_NAMES,
} from './types'
import { HULL_BAND_IDS, HULL_ROLE_IDS, hullLinesSchema, type HullBand, type HullLines, type HullRoleId, type HullTemplateCell } from './hullLines'
import { sinksConfigSchema, type SinksConfig } from './sinks/schema'
import { WARE_COUNT, wareIndex, type WareId } from './wares'

/** The raw JSON of every strategic data file, keyed by file (unparsed; tests pass broken copies). */
export interface RawStrategicFiles {
  wares: unknown
  modules: unknown
  templates: unknown
  economySeed: unknown
  hulls: unknown
  roles: unknown
  jobs: unknown
  factions: unknown
  strategic: unknown
  travel: unknown
  combat: unknown
  /** M8 demand sinks (plan 2026-10-04 SNK-1). */
  sinks: unknown
  gate: unknown
  wire: unknown
  local: unknown
  /** M10 P2b2: the hull authoring source (P2a1), parsed for the derived hull tables. */
  hullLines: unknown
}

/** The raw JSON of the M5 Charter data files (M5 plan C0), kept apart from M4's set. */
export interface RawCharterFiles {
  claims: unknown
  states: unknown
  ladder: unknown
  ai: unknown
  outlaws: unknown
  law: unknown
  votes: unknown
  zones: unknown
}

/** The bundled M5 Charter data files. */
export const CHARTER_FILES: Readonly<RawCharterFiles> = {
  claims: claimsJson,
  states: statesJson,
  ladder: ladderJson,
  ai: aiJson,
  outlaws: outlawsJson,
  law: lawJson,
  votes: votesJson,
  zones: zonesJson,
}

/** The M5 Charter tuning (M5 spec §4-§12), parsed and cross-checked, with the index tables the M5 slices read. */
export interface CharterData {
  claims: ClaimsConfig
  states: StatesConfig
  ladder: LadderConfig
  ai: AiConfig
  outlaws: OutlawsConfig
  law: LawConfig
  votes: VotesConfig
  zones: ZonesConfig
  /** Factions in factions.json (the row length of the per-faction tables below). */
  factionCount: number
  /** `f × TRAIT_COUNT + TRAIT.x`: personality weights 0..1. */
  personality: Float32Array
  /** Per faction: 1 = outlaw (no claims, Charter money or votes). */
  outlaw: Uint8Array
  /** Per faction: the parent faction index, −1 = none. */
  parent: Int8Array
  /** Per faction: the hidden escalation bias (0 = none). */
  escalationBias: Float32Array
  /** `a × factionCount + b`: the starting faction-to-faction standing (symmetric). */
  startStanding: Float32Array
  /** `a × factionCount + b`: 1 = the standing is hidden (never shown in the UI). */
  hiddenStanding: Uint8Array
  /** Per hull (hulls.json order): its toll class (HULL_CLASS). */
  hullTollClass: Uint8Array
}

/** The raw JSON of the M6 data files (plan P0), kept apart from M4's and M5's sets. */
export interface RawFleetFiles {
  fleet: unknown
  orders: unknown
  captains: unknown
  corp: unknown
  commission: unknown
  wireM6: unknown
}

/** The bundled M6 data files. */
export const FLEET_FILES: Readonly<RawFleetFiles> = {
  fleet: fleetJson,
  orders: ordersJson,
  captains: captainsJson,
  corp: corpJson,
  commission: commissionJson,
  wireM6: wireM6Json,
}

/** The M6 tuning (M6 spec §4-§8), parsed and cross-checked, with the index tables the M6 code reads. */
export interface FleetData {
  fleet: FleetConfig
  orders: OrdersConfig
  captains: CaptainsConfig
  corp: CorpConfig
  commission: CommissionConfig
  wire: WireM6Config
  /** Per hull (hulls.json order): its SHIP_CLASS, −1 = not sold to the player. */
  classOfHull: Int8Array
  /** Per STATION_TYPE: its template index, −1 = not yet in templates.json (only `defense_platform`, until P6). */
  stationTypeTemplate: Int16Array
  /** orders.json preset id → index (FleetStore.preset = index + 1). */
  presetIndex: ReadonlyMap<string, number>
}

/** The bundled data files. */
export const STRATEGIC_FILES: Readonly<RawStrategicFiles> = {
  wares: waresJson,
  modules: modulesJson,
  templates: templatesJson,
  economySeed: economySeedJson,
  hulls: hullsJson,
  roles: rolesJson,
  jobs: jobsJson,
  factions: factionsJson,
  strategic: strategicJson,
  travel: travelJson,
  combat: combatJson,
  sinks: sinksJson,
  gate: gateJson,
  wire: wireJson,
  local: localJson,
  hullLines: hullLinesJson,
}

/** A recipe resolved to per-ware arrays (quantities per cycle at level 1). */
export interface ResolvedRecipe {
  id: string
  /** Index into `StrategicData.recipes`. */
  index: number
  /** Index of its module in `modules.modules`. */
  module: number
  cycleS: number
  /** WARE_COUNT tonnes per cycle (0 = not used). */
  inputs: Float32Array
  outputs: Float32Array
  /** Ware indices with a non-zero input / output, in ware order. */
  inputWares: readonly number[]
  outputWares: readonly number[]
  /** R2-T1: a ware on both sides is netted (the cycle is powered from its own output). */
  selfFed: boolean
  /** R2-T1 (D-335 D3): the line runs while any output has room; full outputs are clamped. */
  partialOutput: boolean
  /** 5(e) (MC-e2): the outputs carry TRADE_BIT.ownerOnly (only the station's owner takes them). */
  ownerOnly: boolean
  /** 5(e) (MC-e7b4): the outputs carry TRADE_BIT.noPilot (AI factions buy them; the pilot and the corporation do not). */
  noPilot: boolean
  /** 5(e) (MC-e2): the input whose lack leaves the line dormant (no starved event or seconds), or -1. */
  dormantWare: number
}

/** M10 P2b2: "none" in the per-hull derived tables (a hull not in hull-lines, or a field it has no value for). */
export const HULL_NONE = 255

/** M10 P2b2: the hull-lines flag bits in `StrategicData.hullFlags`. Append-only (M10 P4b1a1 adds relic and retired). */
export const HULL_FLAG = { export: 1, story: 2, unique: 4, relic: 8, retired: 16 } as const

/** M10 P2b2: hull-lines `sizes` by band index (`HULL_BAND_IDS` order). */
export interface HullSizeTables {
  /** The band's toll class (`HULL_CLASS`). */
  tollClass: Uint8Array
  /** 1 = a hull of this band can be claimed. */
  claimable: Uint8Array
}

/**
 * M10 P2d2: false when hull `hull`'s band is known and its hull-lines `sizes` row is not claimable (L and XL). A hull
 * with no band (`HULL_NONE`: not in hull-lines) is not refused here; the class check still applies. Pure, no rng.
 * Every legacy row is S or M, so it stays claimable.
 */
export function bandClaimable(data: Pick<StrategicData, 'hullBand' | 'hullSizes'>, hull: number): boolean {
  const b = data.hullBand[hull]
  return b === undefined || b === HULL_NONE || data.hullSizes.claimable[b] === 1
}

/** The per-hull and per-band tables derived from hull-lines, built once at load. */
type HullTables = Pick<StrategicData, 'hullBand' | 'hullRole' | 'hullMaker' | 'hullLine' | 'hullFlags' | 'lineIds' | 'hullSizes'>

export interface StrategicData {
  wares: WaresFile
  modules: ModulesFile
  templates: TemplatesFile
  economySeed: EconomySeedFile
  hulls: HullsFile
  roles: RolesFile
  jobs: JobsFile
  factions: FactionsFile
  strategic: StrategicConfig
  travel: TravelConfig
  combat: CombatConfig
  /** M8 demand sinks (plan 2026-10-04 SNK-1): repairs, munitions and works; all off until SNK-7. */
  sinks: SinksConfig
  gate: GateConfig
  wire: WireConfig
  local: LocalConfig
  /** WARE_COUNT base prices per t. */
  basePrice: Float64Array
  /** Every recipe of every production module, in module then recipe order. */
  recipes: readonly ResolvedRecipe[]
  /** Recipe indices producing each ware (`producers[w]`), for upstream cause attribution. */
  producers: readonly (readonly number[])[]
  moduleIndex: ReadonlyMap<string, number>
  templateIndex: ReadonlyMap<string, number>
  hullIndex: ReadonlyMap<string, number>
  recipeIndex: ReadonlyMap<string, number>
  /** `hull × WARE_COUNT + ware`: build cost in t. */
  hullCost: Float32Array
  /** Per hull: build cost at base prices (loss values, prices). */
  hullValue: Float64Array
  /** M10 P2b2: `hull-lines.json`, parsed (the source of the derived hull tables below). */
  hullLines: HullLines
  /** Per hull: its size band (index into `HULL_BAND_IDS`), `HULL_NONE` = not in hull-lines. */
  hullBand: Uint8Array
  /** Per hull: its design role (index into `HULL_ROLE_IDS`), `HULL_NONE` = none (unlined, or a story line). */
  hullRole: Uint8Array
  /** Per hull: its maker (faction index), `HULL_NONE` = none (unlined). */
  hullMaker: Uint8Array
  /** Per hull: its maker line (index into `lineIds`), `HULL_NONE` = none (unlined). */
  hullLine: Uint8Array
  /** Per hull: its `HULL_FLAG` bits (0 for a hull not in hull-lines, so a bit test never reads "none" as set). */
  hullFlags: Uint8Array
  /** The hull-lines line ids, in file order. */
  lineIds: readonly string[]
  /** Per band (index into `HULL_BAND_IDS`): the `sizes` rules the band rules read. */
  hullSizes: HullSizeTables
  /** M5: the Charter data files and their tables. */
  charter: CharterData
  /** M6: the fleet, orders, captains, corporation, commissioning and wire-m6 files and their tables. */
  fleet: FleetData
}

function indexById(items: readonly { id: string }[]): Map<string, number> {
  return new Map(items.map((item, i) => [item.id, i]))
}

function wareArray(qty: Partial<Record<WareId, number>>): { arr: Float32Array; wares: number[] } {
  const arr = new Float32Array(WARE_COUNT)
  for (const id of Object.keys(qty) as WareId[]) arr[wareIndex(id)] = qty[id] ?? 0
  const wares: number[] = []
  for (let w = 0; w < WARE_COUNT; w++) if (arr[w] > 0) wares.push(w)
  return { arr, wares }
}

function refError(file: string, what: string): Error {
  return new Error(`strategic data: ${file}: ${what}`)
}

/** Throws unless `id` is in `known`, naming the file, the context and the reference. */
function need(known: ReadonlyMap<string, number>, id: string, file: string, context: string, kind: string): void {
  if (!known.has(id)) throw refError(file, `${context} names unknown ${kind} "${id}"`)
}

/** Throws unless the faction `id` exists and is (or is not) an outlaw. */
function needFaction(factions: FactionsFile, id: string, file: string, context: string, outlaw: boolean): number {
  const f = factions.factions.findIndex((d) => d.id === id)
  if (f < 0) throw refError(file, `${context} names unknown faction "${id}"`)
  if (factions.factions[f].outlaw !== outlaw) throw refError(file, `${context} faction "${id}" must ${outlaw ? '' : 'not '}be an outlaw`)
  return f
}

/** Parses and cross-checks the M5 Charter files against the parsed M4 factions and hulls (M5 plan C0). */
export function parseCharterData(raw: RawCharterFiles, factions: FactionsFile, hulls: HullsFile): CharterData {
  const claims = claimsConfigSchema.parse(raw.claims)
  const states = statesConfigSchema.parse(raw.states)
  const ladder = ladderConfigSchema.parse(raw.ladder)
  const ai = aiConfigSchema.parse(raw.ai)
  const outlaws = outlawsConfigSchema.parse(raw.outlaws)
  const law = lawConfigSchema.parse(raw.law)
  const votes = votesConfigSchema.parse(raw.votes)
  const zones = zonesConfigSchema.parse(raw.zones)
  const hullIndex = indexById(hulls.hulls)

  // References into hulls.json and factions.json.
  for (const id of Object.keys(claims.toll.hullClass)) need(hullIndex, id, 'claims.json', 'toll.hullClass', 'hull')
  for (const id of Object.keys(ladder.warBand.hulls)) need(hullIndex, id, 'ladder.json', 'warBand.hulls', 'hull')
  needFaction(factions, ladder.referee, 'ladder.json', 'referee', false)
  needFaction(factions, ai.stoke.faction, 'ai.json', 'stoke.faction', false)
  // LV WR-1a follow-up (D-TBD): the peace stretch's simmer cap must sit below the ladder's war entry, or press lifts
  // the pair into war inside the stretch. ai.json and ladder.json parse separately, so the cross-file check is here.
  const waves = ai.press.waves
  if (waves !== undefined && waves.simmerCap >= ladder.relations.war.up) {
    throw refError('ai.json', `press.waves.simmerCap ${waves.simmerCap} must be below ladder.json relations.war.up ${ladder.relations.war.up}`)
  }
  needFaction(factions, law.fines.faction, 'law.json', 'fines.faction', false)
  needFaction(factions, votes.chair, 'votes.json', 'chair', false)
  for (const id of states.megacorps) {
    const f = needFaction(factions, id, 'states.json', 'megacorps', false)
    if (factions.factions[f].kind !== 'megacorp') throw refError('states.json', `megacorps names "${id}", which is not a megacorp`)
  }
  const g = needFaction(factions, outlaws.guardian.faction, 'outlaws.json', 'guardian.faction', true)
  const parentOfGuardian = factions.factions[g].parent
  if (parentOfGuardian !== outlaws.guardian.parent) {
    throw refError('outlaws.json', `guardian.parent "${outlaws.guardian.parent}" but factions.json gives "${String(parentOfGuardian)}"`)
  }

  // Per-faction tables.
  const n = factions.factions.length
  const personality = new Float32Array(n * TRAIT_COUNT)
  const outlaw = new Uint8Array(n)
  const parent = new Int8Array(n).fill(-1)
  const escalationBias = new Float32Array(n)
  const ids = factions.factions.map((f) => f.id as string)
  factions.factions.forEach((f, i) => {
    for (let t = 0; t < TRAIT_COUNT; t++) personality[i * TRAIT_COUNT + t] = f.personality[TRAIT_NAMES[t]]
    outlaw[i] = f.outlaw ? 1 : 0
    if (f.parent !== null) parent[i] = ids.indexOf(f.parent)
    escalationBias[i] = f.escalationBias ?? 0
  })
  const startStanding = new Float32Array(n * n).fill(factions.standings.default)
  const hiddenStanding = new Uint8Array(n * n)
  for (let i = 0; i < n; i++) startStanding[i * n + i] = 100
  for (const p of factions.standings.pairs) {
    const a = ids.indexOf(p.a)
    const b = ids.indexOf(p.b)
    startStanding[a * n + b] = p.value
    startStanding[b * n + a] = p.value
    if (p.hidden === true) {
      hiddenStanding[a * n + b] = 1
      hiddenStanding[b * n + a] = 1
    }
  }
  // M10 P2d2: the row's stamped `tollClass` (legacy rows carry the claims.json map value, new rows their band's),
  // else the claims.json map (a row the compile never stamped, e.g. one a test injects), else medium.
  const hullTollClass = new Uint8Array(hulls.hulls.length).fill(HULL_CLASS.medium)
  hulls.hulls.forEach((h, i) => {
    const c = h.tollClass ?? claims.toll.hullClass[h.id]
    if (c !== undefined) hullTollClass[i] = HULL_CLASS[c]
  })

  return {
    claims,
    states,
    ladder,
    ai,
    outlaws,
    law,
    votes,
    zones,
    factionCount: n,
    personality,
    outlaw,
    parent,
    escalationBias,
    startStanding,
    hiddenStanding,
    hullTollClass,
  }
}

/** Station types whose template may be missing until the task that adds it (P6: `defense_platform`). */
const PENDING_STATION_TEMPLATES: readonly string[] = ['defense_platform']

/**
 * Parses and cross-checks the M6 files (plan P0) against the parsed M4 hulls, templates, factions and caps: each
 * classed hull's cargo inside its class hold range (several hulls may share a class, M10 P2b1), the station types'
 * templates, the registration template, the player slot inside the faction capacity, and the store index widths.
 */
export function parseFleetData(
  raw: RawFleetFiles,
  hulls: HullsFile,
  templates: TemplatesFile,
  factions: FactionsFile,
  strategic: StrategicConfig,
  /** M10 P4b1a1: per hull, the template cell whose ranges type it (`templateCellsOf`); absent = every row legacy. */
  cells: readonly (HullTemplateCell | undefined)[] = [],
): FleetData {
  const fleet = fleetConfigSchema.parse(raw.fleet)
  const orders = ordersConfigSchema.parse(raw.orders)
  const captains = captainsConfigSchema.parse(raw.captains)
  const corp = corpConfigSchema.parse(raw.corp)
  const commission = commissionConfigSchema.parse(raw.commission)
  const wire = wireM6ConfigSchema.parse(raw.wireM6)
  const templateIndex = indexById(templates.templates)

  // hulls.json `class` → fleet.json classes (P5 fills the classes in; M10 P2b1: several hulls may share one).
  const classOfHull = new Int8Array(hulls.hulls.length).fill(-1)
  hulls.hulls.forEach((h, i) => {
    if (h.class === undefined) return
    const c = SHIP_CLASS[h.class]
    // M10 P4b1a1 (review M2): a templated row or a variant checks against its role template's band ranges where the
    // cell has them; a legacy row keeps fleet.json classes. Hull rows carry no price, so `price` is read by P5.
    const cell = cells[i]
    const hold = cell?.hold ?? fleet.classes[h.class].hold
    const by = cell?.hold === undefined ? h.class : `${h.class} template`
    if (h.cargo < hold[0] || h.cargo > hold[1]) throw refError('hulls.json', `hull "${h.id}" cargo ${h.cargo} t is outside the ${by} hold [${hold[0]}, ${hold[1]}]`)
    const crew = cell?.crewRange
    if (crew !== undefined && h.crew !== undefined && (h.crew < crew[0] || h.crew > crew[1])) {
      throw refError('hulls.json', `hull "${h.id}" crew ${h.crew} is outside the ${h.class} template crew [${crew[0]}, ${crew[1]}]`)
    }
    classOfHull[i] = c
  })

  // Station types → templates.json (the type names are the template ids).
  const stationTypeTemplate = new Int16Array(STATION_TYPE_COUNT).fill(-1)
  for (const type of STATION_TYPE_NAMES) {
    const t = templateIndex.get(type)
    if (t === undefined) {
      if (!PENDING_STATION_TEMPLATES.includes(type)) throw refError('commission.json', `station type "${type}" names unknown template "${type}"`)
      continue
    }
    stationTypeTemplate[STATION_TYPE[type]] = t
  }
  need(templateIndex, corp.founding.registrationTemplate, 'corp.json', 'founding.registrationTemplate', 'template')

  // The player slot and the store index widths.
  if (corp.slot.index !== PLAYER_SLOT || corp.slot.index !== factions.factions.length) {
    throw refError('corp.json', `slot.index ${corp.slot.index} must be PLAYER_SLOT (${PLAYER_SLOT}), the first index after factions.json`)
  }
  if (corp.slot.index >= strategic.caps.factionCapacity) throw refError('corp.json', `slot.index ${corp.slot.index} is outside caps.factionCapacity`)
  for (const t of Object.values(corp.tickers)) {
    if (t.length < corp.founding.tickerLength[0] || t.length > corp.founding.tickerLength[1]) {
      throw refError('corp.json', `ticker "${t}" is outside founding.tickerLength`)
    }
  }
  if (strategic.caps.playerShips > 32767 || strategic.caps.playerShips > strategic.caps.maxAgents) {
    throw refError('strategic.json', 'caps.playerShips must fit an Int16 slot index and stay within caps.maxAgents')
  }
  const captainSlots = captains.capacity.hired + strategic.caps.maxStations * captains.roster.size[1]
  if (captainSlots > 32767) throw refError('captains.json', `${captainSlots} captain slots exceed an Int16 index`)
  if (strategic.caps.playerStations > strategic.caps.maxSites) throw refError('strategic.json', 'caps.playerStations must stay within caps.maxSites')

  // Every class can hold (a new ship's default order).
  for (const c of SHIP_CLASS_NAMES) {
    if (!orders.orders[orders.defaultOrder].classes.includes(c)) {
      throw refError('orders.json', `orders.${orders.defaultOrder}.classes (the default order) must include every class (missing "${c}")`)
    }
  }

  return {
    fleet,
    orders,
    captains,
    corp,
    commission,
    wire,
    classOfHull,
    stationTypeTemplate,
    presetIndex: new Map(orders.presets.map((p, i) => [p.id, i])),
  }
}

type HullFlagSource = { export?: boolean; story?: boolean; unique?: boolean; relic?: boolean; retired?: boolean }

function hullFlagsOf(h: HullFlagSource): number {
  let f = 0
  for (const k of Object.keys(HULL_FLAG) as (keyof typeof HULL_FLAG)[]) if (h[k] === true) f |= HULL_FLAG[k]
  return f
}

/**
 * M10 P4b1a1 (review M2; the load rule): per hull (hulls.json order), the role template cell whose class ranges type
 * it, or undefined for a legacy row (it keeps `fleet.json classes`). A templated row is a non-legacy line hull with a
 * role; a variant takes its own `role` or its base's, at its base's band. A cell without the range falls back to the
 * class's in `parseFleetData`.
 */
export function templateCellsOf(lines: HullLines, hulls: HullsFile, hullIndex: ReadonlyMap<string, number>): (HullTemplateCell | undefined)[] {
  const cells: (HullTemplateCell | undefined)[] = new Array<HullTemplateCell | undefined>(hulls.hulls.length).fill(undefined)
  const info = new Map<string, { role: HullRoleId | undefined; band: HullBand }>()
  const cellOf = (role: HullRoleId | undefined, band: HullBand): HullTemplateCell | undefined => (role === undefined ? undefined : lines.roles[role].templates?.[band])
  for (const l of lines.lines) {
    for (const h of l.hulls) {
      info.set(h.id, { role: l.role, band: h.band })
      const i = hullIndex.get(h.id)
      if (i !== undefined && h.legacy !== true) cells[i] = cellOf(l.role, h.band)
    }
  }
  for (const u of lines.unlined) info.set(u.id, { role: undefined, band: u.band })
  for (const v of lines.variants) {
    const b = info.get(v.base)
    if (b === undefined) continue
    const role = v.role ?? b.role
    info.set(v.id, { role, band: b.band })
    const i = hullIndex.get(v.id)
    if (i !== undefined) cells[i] = cellOf(role, b.band)
  }
  return cells
}

/**
 * M10 P4b1a1: hull-lines `buyerMixes` against the rows: every hull exists, isn't retired, lists the job in its
 * `roles`, and an L or XL hull is bought only by its maker (no cross-maker L, plan Q4).
 */
function checkBuyerMixes(lines: HullLines, hulls: HullsFile, hullIndex: ReadonlyMap<string, number>, tables: HullTables, factions: FactionsFile): void {
  if (lines.buyerMixes === undefined) return
  const factionIndex = indexById(factions.factions)
  const lBand = HULL_BAND_IDS.indexOf('L')
  for (const [buyer, jobs] of Object.entries(lines.buyerMixes)) {
    for (const [job, mix] of Object.entries(jobs ?? {})) {
      for (const id of Object.keys(mix ?? {})) {
        const where = `buyerMixes.${buyer}.${job}`
        const i = hullIndex.get(id)
        if (i === undefined) throw refError('hull-lines.json', `${where} names unknown hull "${id}"`)
        const row = hulls.hulls[i]
        if (row.retired === true || (tables.hullFlags[i] & HULL_FLAG.retired) !== 0) throw refError('hull-lines.json', `${where} names retired hull "${id}"`)
        if (!(row.roles as readonly string[]).includes(job)) throw refError('hull-lines.json', `${where} names hull "${id}", whose roles don't list ${job}`)
        const band = tables.hullBand[i]
        if (band !== HULL_NONE && band >= lBand && tables.hullMaker[i] !== factionIndex.get(buyer)) {
          throw refError('hull-lines.json', `${where} names L hull "${id}" of another maker (no cross-maker L)`)
        }
      }
    }
  }
}

/**
 * M10 P2b2: the per-hull band, design role, maker, line and flag tables and the per-band `sizes` tables, derived
 * from hull-lines once at load. A `hulls.json` row missing from hull-lines (tests inject hulls) gets `HULL_NONE`
 * and no flags, and a hull-lines hull with no `hulls.json` row is skipped: coverage is P2a1's data test.
 * Cross-checks every role class against `SHIP_CLASS_NAMES`, and each lined row's `class` against its role: a
 * legacy row's class is any of the role's classes (`hauler_large` is band M until P3c), a compiled row's is the
 * class of its band exactly. A variant takes its base's tables (P4b1 adds its own role and relic fields).
 */
function deriveHullTables(lines: HullLines, hulls: HullsFile, hullIndex: ReadonlyMap<string, number>, factions: FactionsFile): HullTables {
  for (const role of HULL_ROLE_IDS) {
    for (const [band, cls] of Object.entries(lines.roles[role].classByBand)) {
      if (!(SHIP_CLASS_NAMES as readonly string[]).includes(cls)) throw refError('hull-lines.json', `roles.${role}.classByBand.${band} names unknown class "${cls}"`)
    }
  }
  if (lines.lines.length >= HULL_NONE) throw refError('hull-lines.json', `${lines.lines.length} lines exceed a Uint8 line index`)
  const factionIndex = indexById(factions.factions)
  const n = hulls.hulls.length
  const hullBand = new Uint8Array(n).fill(HULL_NONE)
  const hullRole = new Uint8Array(n).fill(HULL_NONE)
  const hullMaker = new Uint8Array(n).fill(HULL_NONE)
  const hullLine = new Uint8Array(n).fill(HULL_NONE)
  const hullFlags = new Uint8Array(n)

  lines.lines.forEach((l, li) => {
    const maker = factionIndex.get(l.maker)
    if (maker === undefined) throw refError('hull-lines.json', `line "${l.id}" names unknown faction "${l.maker}"`)
    const byBand = l.role === undefined ? undefined : lines.roles[l.role].classByBand
    for (const h of l.hulls) {
      const i = hullIndex.get(h.id)
      if (i === undefined) continue
      hullBand[i] = HULL_BAND_IDS.indexOf(h.band)
      hullRole[i] = l.role === undefined ? HULL_NONE : HULL_ROLE_IDS.indexOf(l.role)
      hullMaker[i] = maker
      hullLine[i] = li
      hullFlags[i] = hullFlagsOf(h)
      const cls = hulls.hulls[i].class
      if (byBand === undefined) continue
      if (h.legacy === true ? cls !== undefined && !Object.values(byBand).includes(cls) : cls !== byBand[h.band]) {
        const want = h.legacy === true ? `one of the ${String(l.role)} role's classes` : `the ${String(l.role)} role's ${h.band} class "${String(byBand[h.band])}"`
        throw refError('hull-lines.json', `hull "${h.id}" (line "${l.id}") has class "${String(cls)}", not ${want}`)
      }
    }
  })
  for (const u of lines.unlined) {
    const i = hullIndex.get(u.id)
    if (i === undefined) continue
    hullBand[i] = HULL_BAND_IDS.indexOf(u.band)
    hullFlags[i] = hullFlagsOf(u)
  }
  for (const v of lines.variants) {
    const i = hullIndex.get(v.id)
    const b = hullIndex.get(v.base)
    if (i === undefined || b === undefined) continue
    hullBand[i] = hullBand[b]
    hullRole[i] = hullRole[b]
    hullMaker[i] = hullMaker[b]
    hullLine[i] = hullLine[b]
    // M10 P4b1a1: as the compile stamps it, the base's flags but retired, plus the variant's own; its own role if set.
    if (v.role !== undefined) hullRole[i] = HULL_ROLE_IDS.indexOf(v.role)
    hullFlags[i] = (hullFlags[b] & ~HULL_FLAG.retired) | hullFlagsOf(v)
  }

  const tollClass = new Uint8Array(HULL_BAND_IDS.length)
  const claimable = new Uint8Array(HULL_BAND_IDS.length)
  HULL_BAND_IDS.forEach((band, b) => {
    tollClass[b] = HULL_CLASS[lines.sizes[band].tollClass]
    claimable[b] = lines.sizes[band].claimable ? 1 : 0
  })
  return { hullBand, hullRole, hullMaker, hullLine, hullFlags, lineIds: lines.lines.map((l) => l.id), hullSizes: { tollClass, claimable } }
}

/**
 * Parses and cross-checks the given raw files. Pure; `loadStrategicData` calls it once on the bundled files.
 * `charterRaw` is the M5 Charter set and `fleetRaw` the M6 set (default: the bundled ones).
 */
export function parseStrategicData(
  raw: RawStrategicFiles,
  charterRaw: RawCharterFiles = CHARTER_FILES,
  fleetRaw: RawFleetFiles = FLEET_FILES,
): StrategicData {
  const wares = waresFileSchema.parse(raw.wares)
  const modules = modulesFileSchema.parse(raw.modules)
  const templates = templatesFileSchema.parse(raw.templates)
  const economySeed = economySeedFileSchema.parse(raw.economySeed)
  const hulls = hullsFileSchema.parse(raw.hulls)
  const roles = rolesFileSchema.parse(raw.roles)
  const jobs = jobsFileSchema.parse(raw.jobs)
  const factions = factionsFileSchema.parse(raw.factions)
  const strategic = strategicConfigSchema.parse(raw.strategic)
  const travel = travelConfigSchema.parse(raw.travel)
  const combat = combatConfigSchema.parse(raw.combat)
  const sinks = sinksConfigSchema.parse(raw.sinks)
  const gate = gateConfigSchema.parse(raw.gate)
  const wire = wireConfigSchema.parse(raw.wire)
  const local = localConfigSchema.parse(raw.local)
  const hullLines = hullLinesSchema.parse(raw.hullLines)

  const moduleIndex = indexById(modules.modules)
  const templateIndex = indexById(templates.templates)
  const hullIndex = indexById(hulls.hulls)

  // templates.json → modules.json
  for (const t of templates.templates) {
    for (const m of t.modules) need(moduleIndex, m.module, 'templates.json', `template "${t.id}"`, 'module')
    if (t.anchorModule !== undefined) {
      for (const m of Object.values(t.anchorModule)) need(moduleIndex, m, 'templates.json', `template "${t.id}" anchorModule`, 'module')
    }
    if (t.basket !== undefined && !t.modules.some((m) => modules.modules[moduleIndex.get(m.module) as number].kind === 'trade')) {
      throw refError('templates.json', `template "${t.id}" overrides a basket but has no trade module`)
    }
  }

  // economy-seed.json → templates.json
  const templateLevels = (id: string): number => templates.templates[templateIndex.get(id) as number].levels[1]
  for (const [arch, entries] of Object.entries(economySeed.archetypes)) {
    for (const e of entries) need(templateIndex, e.template, 'economy-seed.json', `archetypes.${arch}`, 'template')
  }
  for (const [anchor, entries] of Object.entries(economySeed.anchors)) {
    for (const e of entries) {
      need(templateIndex, e.template, 'economy-seed.json', `anchors.${anchor}`, 'template')
      if (e.since !== undefined && e.since > economySeed.revision) {
        throw refError('economy-seed.json', `anchors.${anchor}: since ${e.since} is above revision ${economySeed.revision}`)
      }
      if (e.level !== undefined && e.level > templateLevels(e.template)) {
        throw refError('economy-seed.json', `anchors.${anchor}: level ${e.level} exceeds template "${e.template}" max level`)
      }
      const t = templates.templates[templateIndex.get(e.template) as number]
      if (e.m2Id !== undefined && t.m2Id !== e.m2Id) {
        throw refError('economy-seed.json', `anchors.${anchor}: m2Id "${e.m2Id}" but template "${e.template}" plays "${String(t.m2Id)}"`)
      }
    }
  }
  for (const id of Object.keys(economySeed.owners)) need(templateIndex, id, 'economy-seed.json', 'owners', 'template')
  for (const id of Object.keys(economySeed.placement.rules)) need(templateIndex, id, 'economy-seed.json', 'placement.rules', 'template')
  for (const t of templates.templates) {
    if (!(t.id in economySeed.owners)) throw refError('economy-seed.json', `owners has no entry for template "${t.id}"`)
    if (!(t.id in economySeed.placement.rules)) throw refError('economy-seed.json', `placement.rules has no entry for template "${t.id}"`)
  }
  for (const [faction, ids] of Object.entries(economySeed.buildPlans.templates)) {
    for (const id of ids ?? []) need(templateIndex, id, 'economy-seed.json', `buildPlans.templates.${faction}`, 'template')
  }

  // roles.json → hulls.json (each role's hulls must support the role; escorts must be escort hulls)
  for (const role of ROLE_IDS) {
    for (const id of Object.keys(roles[role].hulls)) {
      need(hullIndex, id, 'roles.json', `${role}.hulls`, 'hull')
      const h = hulls.hulls[hullIndex.get(id) as number]
      if (!h.roles.includes(role)) throw refError('roles.json', `${role}.hulls names hull "${id}", which does not fly the ${role} role`)
    }
  }
  for (const id of Object.keys(roles.convoy.escortHulls)) {
    need(hullIndex, id, 'roles.json', 'convoy.escortHulls', 'hull')
    if (!hulls.hulls[hullIndex.get(id) as number].escort) throw refError('roles.json', `convoy.escortHulls names hull "${id}", which is not an escort hull`)
  }

  // sinks.json → factions.json (D-1126): the megaproject fund trigger names known non-outlaw factions.
  for (const id of sinks.works.megaproject?.fundTrigger?.factions ?? []) {
    needFaction(factions, id, 'sinks.json', 'works.megaproject.fundTrigger.factions', false)
  }

  // Resolved tables.
  const basePrice = new Float64Array(WARE_COUNT)
  for (let w = 0; w < WARE_COUNT; w++) basePrice[w] = wares.wares[w].basePrice
  const recipes: ResolvedRecipe[] = []
  modules.modules.forEach((m, mi) => {
    if (m.kind !== 'production') return
    for (const r of m.recipes) {
      const inputs = wareArray(r.inputs)
      const outputs = wareArray(r.outputs)
      recipes.push({
        id: r.id,
        index: recipes.length,
        module: mi,
        cycleS: r.cycleS,
        inputs: inputs.arr,
        outputs: outputs.arr,
        inputWares: inputs.wares,
        outputWares: outputs.wares,
        selfFed: r.selfFed,
        partialOutput: r.partialOutput,
        ownerOnly: r.ownerOnly,
        noPilot: r.noPilot,
        dormantWare: r.dormantOn === undefined ? -1 : wareIndex(r.dormantOn),
      })
    }
  })
  const producers: number[][] = Array.from({ length: WARE_COUNT }, () => [])
  for (const r of recipes) for (const w of r.outputWares) producers[w].push(r.index)
  const hullCost = new Float32Array(hulls.hulls.length * WARE_COUNT)
  const hullValue = new Float64Array(hulls.hulls.length)
  hulls.hulls.forEach((h, hi) => {
    const cost = wareArray(h.buildCost)
    hullCost.set(cost.arr, hi * WARE_COUNT)
    for (const w of cost.wares) hullValue[hi] += cost.arr[w] * basePrice[w]
  })
  const hullTables = deriveHullTables(hullLines, hulls, hullIndex, factions)
  checkBuyerMixes(hullLines, hulls, hullIndex, hullTables, factions)

  return {
    wares,
    modules,
    templates,
    economySeed,
    hulls,
    roles,
    jobs,
    factions,
    strategic,
    travel,
    combat,
    sinks,
    gate,
    wire,
    local,
    basePrice,
    recipes,
    producers,
    moduleIndex,
    templateIndex,
    hullIndex,
    recipeIndex: new Map(recipes.map((r) => [r.id, r.index])),
    hullCost,
    hullValue,
    hullLines,
    ...hullTables,
    charter: parseCharterData(charterRaw, factions, hulls),
    fleet: parseFleetData(fleetRaw, hulls, templates, factions, strategic, templateCellsOf(hullLines, hulls, hullIndex)),
  }
}

/**
 * The balance probe's data patch (plan 2026-10-04 SNK-1, review 2 m5): `tools/strategic/balance-probe.mjs --data
 * <patch.json>` sets this env var for the soak it runs. Its value is the patch as JSON text (never a path). The patch is keyed by data file, from
 * `RawStrategicFiles`, `RawCharterFiles` or `RawFleetFiles` (`{ "sinks": { "repairs": { "enabled": true } }, "ai": { "press": { "truceDays": 0 } } }`), and
 * deep-merged over the bundled files before they are parsed, so it goes through the same Zod schemas. Without the
 * env var (every game build, CI and normal tests) the bundled files load unchanged.
 */
export const PROBE_DATA_ENV = 'STRATEGIC_PROBE_DATA'

/** The probe env var's value, or undefined outside Node (the game's worker has no `process`) or when unset or blank. */
export function probeDataEnv(): string | undefined {
  if (typeof process === 'undefined') return undefined
  const v = process.env[PROBE_DATA_ENV]
  return v !== undefined && v.trim() !== '' ? v : undefined
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** A new value: plain objects merge key by key; anything else in `patch` (numbers, arrays, null) replaces. */
function mergePatch(base: unknown, patch: unknown): unknown {
  if (!isPlainObject(base) || !isPlainObject(patch)) return patch
  const out: Record<string, unknown> = { ...base }
  for (const k of Object.keys(patch)) out[k] = mergePatch(base[k], patch[k])
  return out
}

/** The probe patch `env` (JSON text) as an object keyed by data file; throws when it isn't a JSON object. */
function parseProbePatch(env: string): Record<string, unknown> {
  const patch: unknown = JSON.parse(env)
  if (!isPlainObject(patch)) throw new Error(`strategic data: ${PROBE_DATA_ENV} must be a JSON object keyed by data file`)
  return patch
}

/** `files` with the patch's keys that name one of its files merged in; `files` itself when none does. Never mutates. */
function patchSet<T extends object>(files: Readonly<T>, patch: Record<string, unknown>): T {
  const keys = Object.keys(patch).filter((k) => k in files)
  if (keys.length === 0) return files as T
  const out: Record<string, unknown> = { ...files }
  for (const k of keys) out[k] = mergePatch(out[k], patch[k])
  return out as T
}

/**
 * `files` with the probe patch `env` (JSON text) merged in; `files` itself when `env` is
 * undefined. Throws on a patch that isn't a JSON object or names an unknown file. Never mutates `files`.
 */
export function applyProbePatch(files: Readonly<RawStrategicFiles>, env: string | undefined): RawStrategicFiles {
  if (env === undefined) return files
  const patch = parseProbePatch(env)
  for (const k of Object.keys(patch)) {
    if (!(k in files)) throw new Error(`strategic data: ${PROBE_DATA_ENV} names unknown data file "${k}"`)
  }
  return patchSet(files, patch)
}

/** The three raw data sets `parseStrategicData` reads: the M4 strategic set, the M5 Charter set and the M6 fleet set. */
export interface ProbeDataSets {
  strategic: RawStrategicFiles
  charter: RawCharterFiles
  fleet: RawFleetFiles
}

/**
 * The probe patch `env` (JSON text) merged over all three sets, each file key routed to the set that holds it
 * (probe tooling, liveliness plan §3.4: arms patch Charter keys such as `ai.press` or `outlaws.clans`). With
 * `env` undefined the given sets come back as they are. Throws on a non-object patch or a key no set holds.
 */
export function applyProbePatches(
  env: string | undefined,
  sets: Readonly<ProbeDataSets> = { strategic: STRATEGIC_FILES, charter: CHARTER_FILES, fleet: FLEET_FILES },
): ProbeDataSets {
  if (env === undefined) return { strategic: sets.strategic, charter: sets.charter, fleet: sets.fleet }
  const patch = parseProbePatch(env)
  for (const k of Object.keys(patch)) {
    if (!(k in sets.strategic) && !(k in sets.charter) && !(k in sets.fleet)) {
      throw new Error(`strategic data: ${PROBE_DATA_ENV} names unknown data file "${k}"`)
    }
  }
  return { strategic: patchSet(sets.strategic, patch), charter: patchSet(sets.charter, patch), fleet: patchSet(sets.fleet, patch) }
}

let cached: StrategicData | null = null

/**
 * The bundled strategic data, parsed and cross-checked once per module instance (with the balance probe's patch
 * merged in only when `STRATEGIC_PROBE_DATA` is set).
 */
export function loadStrategicData(): StrategicData {
  if (cached === null) {
    const sets = applyProbePatches(probeDataEnv())
    cached = parseStrategicData(sets.strategic, sets.charter, sets.fleet)
  }
  return cached
}
